/**
 * "Forget…" on a transfer row (S7 review D F4): asks main where the job's remote leftovers are (a preview changes
 * nothing), shows them in the app's confirm dialog — Forget removes only the entry from this list and stops tracking
 * the staged files, which stay in the share — and on confirm drops the job, removes its row and fires
 * `feature:storage_transfer_action { action: 'forget' }` (after success; no names or paths in the event).
 */
import { createElement } from 'react';
import { toast } from '../../../components/ui/toaster';
import { openConfirmDialog } from '../../workspace/common/components/confirm/confirmDialogStore';
import { forgetTransfer, trackTransferEvent } from '../services/transfersApi';
import { useTransfersStore } from '../store/transfersStore';

export const FORGET_EXPLANATION = 'Jam SQL Studio removes this transfer from the list and stops tracking its staged upload files. They stay where they are; delete them yourself if you no longer need them:';

/**
 * One leftover, `<share path> — <what it is>` (main's `describeRemoteState`): the path in a mono block that may break
 * anywhere, what it is in ordinary wrapping (final UI/UX batch C1, R15 / N21 — `break-all` on the whole list split
 * "this" and "release" mid-word). A line without a path ("and 3 more") is plain text.
 */
function leftBehindItem(line: string) {
  const separator = line.indexOf(' — ');
  if (separator < 0) return createElement('li', { key: line, className: 'text-xs' }, line);
  return createElement('li', { key: line, className: 'space-y-0.5' },
    createElement('code', { className: 'block rounded bg-muted px-1.5 py-0.5 font-mono text-xs [overflow-wrap:anywhere]', 'data-testid': 'transfer-forget-path' }, line.slice(0, separator)),
    createElement('span', { className: 'text-xs text-muted-foreground' }, line.slice(separator + 3)));
}

function leftBehindList(lines: string[]) {
  return createElement('div', { className: 'space-y-2', 'data-testid': 'transfer-forget-confirmation' },
    createElement('p', null, FORGET_EXPLANATION),
    createElement('ul', { className: 'space-y-1.5' }, ...lines.map(leftBehindItem)));
}

export async function requestForgetTransfer(jobId: string): Promise<void> {
  const preview = await forgetTransfer(jobId, false);
  if (!preview.ok) { toast.error({ title: 'Forget is not available', description: preview.error.message }); return; }
  openConfirmDialog({
    title: 'Forget this transfer?',
    description: leftBehindList(preview.value.leftBehind),
    confirmLabel: 'Forget',
    variant: 'destructive',
    onConfirm: async () => {
      const job = useTransfersStore.getState().jobs[jobId];
      const result = await forgetTransfer(jobId, true);
      if (!result.ok) { toast.error({ title: 'Forget failed', description: result.error.message }); return; }
      useTransfersStore.getState().forget(jobId);
      trackTransferEvent('feature:storage_transfer_action', job ? { action: 'forget', kind: job.kind, origin: job.origin, scope: job.scope } : { action: 'forget' });
      toast.warning({ title: 'Transfer forgotten', description: `Left in place: ${result.value.leftBehind.join('; ')}` });
    },
  });
}
